import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import type { TrackPoint } from "../../../../lib/offline-draft";

type NativeGeoArPosition = {
  sessionId: string;
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  timestamp: number;
  provider: string;
  cached: boolean;
};

type NativeGeoArLocationError = {
  sessionId: string;
  message: string;
};

type TheraGeoArLocationPlugin = {
  startGeoArPositionUpdates(options: { sessionId: string }): Promise<{
    active: boolean;
    sessionId: string;
    providers: string[];
  }>;
  stopGeoArPositionUpdates(options: { sessionId: string }): Promise<{ active: boolean; stopped: boolean }>;
  addListener(
    eventName: "geoArPosition",
    listener: (position: NativeGeoArPosition) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "geoArLocationError",
    listener: (error: NativeGeoArLocationError) => void,
  ): Promise<PluginListenerHandle>;
};

export type GeoArStandalonePositionCallbacks = {
  onPosition: (position: TrackPoint) => void;
  onError: (message: string) => void;
};

export type StopGeoArStandalonePosition = () => Promise<void>;

const nativeLocation = registerPlugin<TheraGeoArLocationPlugin>("TheraLocation");
let latestAndroidSessionId: string | null = null;

function normalizeNativePosition(position: NativeGeoArPosition): TrackPoint | null {
  if (!Number.isFinite(position.latitude) || !Number.isFinite(position.longitude)) return null;
  const accuracy = Number(position.accuracy);
  const timestamp = Number(position.timestamp);
  const altitude = position.altitude === null || position.altitude === undefined
    ? null
    : Number(position.altitude);
  return {
    latitude: position.latitude,
    longitude: position.longitude,
    altitude: altitude !== null && Number.isFinite(altitude) ? altitude : null,
    accuracy: Number.isFinite(accuracy) ? Math.max(0, accuracy) : 0,
    timestamp: Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now(),
  };
}

function webLocationError(error: GeolocationPositionError) {
  if (error.code === error.PERMISSION_DENIED) return "Permiso de ubicación no concedido.";
  if (error.code === error.TIMEOUT) {
    return "Aún no hay señal GPS. Mantén Geo AR abierto y prueba en un lugar con cielo visible.";
  }
  return "No fue posible obtener la posición actual.";
}

/**
 * Starts a location stream owned exclusively by the open Geo AR session.
 * It neither starts nor attaches to THERA route recording.
 */
export async function startGeoArStandalonePosition(
  callbacks: GeoArStandalonePositionCallbacks,
): Promise<StopGeoArStandalonePosition> {
  if (Capacitor.getPlatform() === "android") {
    const sessionId = crypto.randomUUID();
    latestAndroidSessionId = sessionId;
    let positionListener: PluginListenerHandle | null = null;
    let errorListener: PluginListenerHandle | null = null;
    try {
      [positionListener, errorListener] = await Promise.all([
        nativeLocation.addListener("geoArPosition", (rawPosition) => {
          if (rawPosition.sessionId !== sessionId) return;
          const position = normalizeNativePosition(rawPosition);
          if (position) callbacks.onPosition(position);
        }),
        nativeLocation.addListener("geoArLocationError", (error) => {
          if (error.sessionId === sessionId) callbacks.onError(error.message);
        }),
      ]);
      if (latestAndroidSessionId !== sessionId) throw new Error("GEO_AR_LOCATION_SESSION_REPLACED");
      await nativeLocation.startGeoArPositionUpdates({ sessionId });
    } catch (cause) {
      if (latestAndroidSessionId === sessionId) latestAndroidSessionId = null;
      await Promise.all([
        positionListener?.remove().catch(() => undefined),
        errorListener?.remove().catch(() => undefined),
        nativeLocation.stopGeoArPositionUpdates({ sessionId }).catch(() => undefined),
      ]);
      throw cause;
    }

    let stopped = false;
    return async () => {
      if (stopped) return;
      stopped = true;
      if (latestAndroidSessionId === sessionId) latestAndroidSessionId = null;
      await Promise.all([
        positionListener?.remove().catch(() => undefined),
        errorListener?.remove().catch(() => undefined),
        nativeLocation.stopGeoArPositionUpdates({ sessionId }).catch(() => undefined),
      ]);
    };
  }

  if (!navigator.geolocation) throw new Error("Este dispositivo no ofrece geolocalización.");
  const watchId = navigator.geolocation.watchPosition(
    (position) => callbacks.onPosition({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      altitude: position.coords.altitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp,
    }),
    (error) => callbacks.onError(webLocationError(error)),
    { enableHighAccuracy: true, maximumAge: 2_000, timeout: 15_000 },
  );
  let stopped = false;
  return async () => {
    if (stopped) return;
    stopped = true;
    navigator.geolocation.clearWatch(watchId);
  };
}
